import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

const applyThemeToDocument = (theme: Theme) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);

  if (theme === 'dark') {
    root.classList.add('dark');
    root.style.backgroundColor = '#050505';
    root.style.setProperty('--dino-hue', '120deg');
  } else {
    root.classList.remove('dark');
    root.style.backgroundColor = '#f8fafc';
    root.style.setProperty('--dino-hue', '0deg');
  }

  const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (favicon) {
    favicon.href = theme === 'dark' ? '/DDDlogo Dark.png' : '/DDDlogo.png';
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'dark';
    const savedTheme = localStorage.getItem('theme') as Theme | null;
    return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    applyThemeToDocument(theme);
  }, [theme]);

  const toggleTheme = () => {
    const next: Theme = theme === 'light' ? 'dark' : 'light';

    // View Transitions API: one hardware-accelerated crossfade for the entire
    // page instead of animating every element's colour individually.
    // Falls back to instant swap on browsers that don't support it.
    const docAny = document as unknown as {
      startViewTransition?: (cb: () => void) => { finished: Promise<void> };
    };

    if (typeof docAny.startViewTransition === 'function') {
      docAny.startViewTransition(() => {
        setTheme(next);
        // applyThemeToDocument runs in the layout effect after setTheme,
        // but for the View Transition we need it inside the callback so the
        // browser captures the post-swap snapshot correctly.
        applyThemeToDocument(next);
      });
    } else {
      setTheme(next);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}; 